import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles } from "lucide-react";

export const SECTOR_TO_SPECIALITY = { cqc: "CQC", ofsted: "Ofsted", aesthetics: "Aesthetics", healthcare: "Healthcare", fostering: "Fostering" };

export function useFounderPlaces() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/founders/places").then((r) => setData(r.data)).catch(() => {}); }, []);
  return data;
}

export const founderPrice = (value, user) => (user?.founder && value != null ? Math.round(value * (100 - user.founder.percent_off)) / 100 : null);

export function FounderBadge({ className = "" }) {
  const { user } = useAuth();
  if (!user?.founder) return null;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-900 border border-amber-300 pl-1 pr-3 py-1 text-xs font-bold ${className}`} data-testid="founder-badge">
      <img src="/founders-club-logo.webp" alt="Futuregen Founders Club" className="w-6 h-6 rounded-full border border-amber-200 bg-white" />
      {user.founder.confirmed ? `Founding Member #${user.founder.number}` : "Founders Club · place reserved"} · {user.founder.speciality_label}
    </span>
  );
}

export function FoundersBanner({ speciality, compact = false }) {
  const { user } = useAuth();
  const data = useFounderPlaces();
  if (!data) return null;
  const rows = speciality ? data.places.filter((p) => p.speciality === speciality) : data.places;
  const anyLeft = rows.some((p) => p.left > 0);
  if (user?.founder) {
    return (
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-center gap-3" data-testid="founders-banner-member">
        <img src="/founders-club-logo.webp" alt="Futuregen Founders Club" className="w-12 h-12 rounded-full shrink-0 border border-amber-200 bg-white object-cover" />
        <p className="text-sm text-amber-950"><span className="font-bold">{user.founder.confirmed ? `You're Founding Member #${user.founder.number}.` : "Your Founders Club place is reserved — it's numbered and counted from your first payment."}</span> {data.percent_off}% off every subscription for as long as you stay fully subscribed, renewals included, plus {data.percent_off}% off your first policy pack{user.founder.pack_discount_available ? "" : " (used)"}.</p>
      </div>
    );
  }
  if (!anyLeft) return null;
  return (
    <div className={`rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 ${compact ? "p-4" : "p-5 sm:p-6"}`} data-testid="founders-banner">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex items-start gap-3">
          <img src="/founders-club-logo.webp" alt="Futuregen Founders Club" className="w-16 h-16 rounded-full shrink-0 border border-amber-200 bg-white object-cover" data-testid="founders-logo" />
          <div>
            <p className="font-display font-extrabold text-amber-950 flex items-center gap-2">Founders Club <Sparkles className="w-4 h-4 text-amber-600" /></p>
            <p className="text-sm text-amber-900/90">Just {data.campaign_total} founder places — {data.total} in each speciality. Founders get <span className="font-bold">{data.percent_off}% off every plan for as long as you stay fully subscribed</span> — renewals stay at the same rate. Pay from day one (no free trial), 12-month minimum term from the first payment — cancelling inside it forfeits the discount and place. Plus {data.percent_off}% off your first policy pack. One place per registered location; places are counted from the first payment.</p>
          </div>
        </div>
        {!user && <Link to="/register" className="shrink-0 inline-flex items-center justify-center rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 transition-colors" data-testid="founders-banner-cta">Claim a founder place</Link>}
      </div>
      <ul className={`mt-4 grid gap-2 ${rows.length > 1 ? "sm:grid-cols-5" : ""}`} data-testid="founders-places">
        {rows.map((p) => (
          <li key={p.speciality} className="rounded-lg bg-white/70 border border-amber-200 px-3 py-2" data-testid={`founders-places-${p.speciality.toLowerCase()}`}>
            <p className="text-[11px] font-semibold text-amber-900/70 truncate">{p.label}</p>
            <p className="text-sm font-bold text-amber-950">{p.left > 0 ? `${p.taken} taken · ${p.left} of ${p.total} left` : "Full"}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
